/**
 * Player: vanilla Minecraft (Java Edition) movement. Physics runs in 20 Hz
 * game ticks with the original constants and update order — input
 * acceleration, move + collide, then gravity and drag — so walk/sprint speeds,
 * jump arcs, air control, sneaking, swimming and creative flight match the
 * source game. The camera interpolates between ticks.
 */

import { Glider } from './elytra';
import { I } from '../game/itemIds';
import * as THREE from 'three';
import {
  AIR_ACCEL, DEFAULT_SLIPPERINESS, DOUBLE_TAP_TICKS, FLY_SPEED, FLY_VERTICAL_DRAG, FOV, GRAVITY,
  HORIZONTAL_DRAG, INPUT_SCALE, JUMP_DELAY_TICKS, JUMP_VELOCITY, LAVA_DRAG, LIQUID_ACCEL,
  LIQUID_EXIT_VELOCITY, LIQUID_SURFACE_HEIGHT, LIQUID_SWIM_IMPULSE, MOUSE_SENSITIVITY, PLAYER_EYE,
  PLAYER_HEIGHT, PLAYER_WIDTH, SNEAK_EYE, SNEAK_HEIGHT, SNEAK_INPUT_SCALE, SPRINT_AIR_ACCEL,
  SPRINT_JUMP_BOOST, SPRINT_SPEED_MULT, STEP_HEIGHT, SWIM_EYE, SWIM_HEIGHT, VERTICAL_DRAG,
  WALK_SPEED, WATER_DRAG, WATER_SPRINT_DRAG, WORLD_HEIGHT,
} from '../constants';
import {
  BLUE_ICE_ID, CLIMBABLE_NETHER, IS_LAVA, LAVA_AMOUNT, collisionOf, FULL_CELL_BOXES, ICE_ID, IS_SOLID, IS_TARGETABLE, IS_WATERY, LAVA_ID,
  NO_BOXES, PACKED_ICE_ID, TARGET_BOXES, PLANT_JITTER, SOUL_SAND_ID, WATER_AMOUNT, WATER_ID,
} from '../blocks';
import { plantJitterX, plantJitterZ } from '../shapes';
import type { World } from '../world/chunk';
import { boxHits, clipMove, type ShapesAt } from '../world/collide';
import { type BlockReader, waterFlow } from '../world/fluid';
import type { PlayerPoseView } from '../game/types';

const HALF_W = PLAYER_WIDTH / 2;
/** Boxes this close to a block face count as touching, not overlapping. */
const EPS = 1e-7;

const STAND = 0;
const CROUCH = 1;
const SWIM = 2;
const POSE_HEIGHT = [PLAYER_HEIGHT, SNEAK_HEIGHT, SWIM_HEIGHT];
const POSE_EYE = [PLAYER_EYE, SNEAK_EYE, SWIM_EYE];

/** Ground friction of the block underfoot (ice is slippery). */
const SLIPPERINESS = new Float32Array(IS_SOLID.length).fill(DEFAULT_SLIPPERINESS);
SLIPPERINESS[ICE_ID] = 0.98;
SLIPPERINESS[PACKED_ICE_ID] = 0.98;
SLIPPERINESS[BLUE_ICE_ID] = 0.989;

/** Horizontal velocity multiplier of the block underfoot (soul sand drags). */
const SPEED_FACTOR = new Float32Array(IS_SOLID.length).fill(1);
SPEED_FACTOR[SOUL_SAND_ID] = 0.4;

/** Velocity added per tick by a full-strength water current. */
const WATER_PUSH = 0.014;
/** Smallest push a current gives a player who is standing still. */
const MIN_WATER_PUSH = 0.0045;

const MOVE_KEYS = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD']);

/** Ticks the damage camera shake lasts (vanilla `maxHurtTime`). */
const HURT_DURATION = 10;
/** Peak tilt of the damage camera shake, in degrees. Vanilla uses 14; dialled
 *  back here because the full-strength roll reads as too violent in first person. */
const HURT_TILT_DEG = 5;
/** Fall distance at which vanilla starts dealing fall damage (and so shakes). */
const FALL_HURT_DISTANCE = 3;
/** Vanilla scales the distance walked by 0.6 before it drives the view bob. */
const WALK_DIST_SCALE = 0.6;
/** Vanilla caps the smoothed walk speed that scales the view bob at 0.1
 *  (`bob`); trimmed here so the sway reads a little softer. */
const BOB_SPEED_CAP = 0.07;
/** Scratch axis / rotation for the hurt tilt, reused every frame. */
const HURT_AXIS = new THREE.Vector3();
const HURT_ROT = new THREE.Quaternion();
const X_AXIS = new THREE.Vector3(1, 0, 0);
const Z_AXIS = new THREE.Vector3(0, 0, 1);
const BOB_ROT = new THREE.Quaternion();
const DEG = Math.PI / 180;

/** Third-person camera distance (vanilla 4) and the clearance kept from walls. */
const THIRD_PERSON_DIST = 4;
const CAM_CLEARANCE = 0.1;
/** Vanilla arm swing length in ticks. */
const SWING_TICKS = 6;
/** Eye height of the dying pose. */
const DEAD_EYE = 0.2;
const CAM_OFFSETS: ReadonlyArray<readonly [number, number, number]> = (() => {
  const o: Array<[number, number, number]> = [];
  for (const x of [-1, 1]) for (const y of [-1, 1]) for (const z of [-1, 1]) o.push([x * CAM_CLEARANCE, y * CAM_CLEARANCE, z * CAM_CLEARANCE]);
  return o;
})();
const CAM_DIR = new THREE.Vector3();
const CAM_ORIGIN = new THREE.Vector3();
const CAM_EYE = new THREE.Vector3();

/** Distance the player's own movement cost since the last `movementExhaustion()`. */
export interface MovementExhaustion {
  walked: number; sprinted: number; swum: number; jumps: number; sprintJumps: number;
}

/** Signed shortest rotation from angle `a` to `b`, in (-PI, PI]. */
function shortestAngle(a: number, b: number): number {
  let d = (b - a) % (2 * Math.PI);
  if (d > Math.PI) d -= 2 * Math.PI;
  else if (d <= -Math.PI) d += 2 * Math.PI;
  return d;
}

export class Player {
  readonly camera: THREE.PerspectiveCamera;
  /** Feet position (bottom centre of the hitbox). */
  position = new THREE.Vector3(8.5, 80, 8.5);
  /** Blocks per tick. */
  velocity = new THREE.Vector3();
  yaw = Math.PI;
  pitch = -0.15;
  onGround = false;
  flying = true;
  /** Ghost mode: flight that passes through blocks, like vanilla spectator. */
  spectator = false;
  inWater = false;
  headInWater = false;
  sprinting = false;
  sneaking = false;
  swimming = false;
  /** Ticks left of the damage camera shake; 0 once the view has settled. */
  hurtTime = 0;

  /** 0 first person, 1 third-person back, 2 third-person front (V key). */
  cameraMode: 0 | 1 | 2 = 0;
  /** Mouse-look multiplier on MOUSE_SENSITIVITY (the lead sets it from options). */
  sensitivity = 1;
  /** Base vertical FOV in degrees (the lead sets it from options). */
  baseFov = FOV;
  /** Survival: flight is locked off (F / double-jump / spectator keys ignored). */
  survival = false;
  /** Options > View Bobbing. */
  viewBobbing = true;
  /** Bedrock auto-jump: hop 1-block ledges when walking into them. Set from settings. */
  autoJump = true;
  /** Camera ease after a step-up: eye lags the feet by this many blocks, decaying per tick. */
  private stepEase = 0;
  private prevStepEase = 0;
  /** Dead: no input, death camera. The lead sets it from PlayerStats; teleportTo() clears it. */
  dead = false;
  /** Arm swing 0..1 (0 = idle); refreshed every tick and every updateCamera(). */
  swingProgress = 0;
  /** Vanilla walk animation phase / smoothed amplitude (per tick). */
  limbSwing = 0;
  limbSwingAmount = 0;
  /** Called on landing with the distance fallen (>0); replaces the built-in fall shake when set. */
  onFall: ((distance: number) => void) | null = null;
  /** In lava (read by the stats tick). */
  inLava = false;
  /** Without pointer lock V is ignored (UI is open); dev pages turn this off. */
  cameraKeyNeedsLock = true;

  private keys = new Set<string>();
  /** Keys pressed since the last tick, so taps shorter than a tick still count. */
  private pressed = new Set<string>();
  private frozen = true;

  private prevPosition = new THREE.Vector3();
  private pose = STAND;
  private height = PLAYER_HEIGHT;
  private eyeHeight = PLAYER_EYE;
  private prevEyeHeight = PLAYER_EYE;
  /** Extra FOV degrees that decay each tick (Lunge burst). */
  fovPulse = 0;
  /** Vanilla-style FOV modifier from movement speed: sprint, speed effects and flight; item use (spear charge included) never alters it. */
  fovModifier(): number {
    return (this.flying ? 1.1 : 1) * ((this.sprinting ? SPRINT_SPEED_MULT : 1) * this.speedMult + 1) / 2;
  }
  private fovScale = 1;
  private prevFovScale = 1;
  /** Distance walked (vanilla `walkDist`): the view bob's phase. */
  private walkDist = 0;
  private prevWalkDist = 0;
  /** Smoothed walk speed that scales the view bob (vanilla `bob`). */
  private bob = 0;
  /** Last camera view-bob phase / strength (0 when off), for the first-person hand. */
  bobPhase = 0;
  bobAmount = 0;
  private prevBob = 0;

  private eyeInWater = false;
  private waterHeight = 0;
  private lavaHeight = 0;
  private shiftDown = false;
  private jumpDelay = 0;
  private sprintTapTimer = 0;
  private flyTapTimer = 0;
  private horizontalCollision = false;
  private minorCollision = false;
  /** World-space direction of this tick's movement input. */
  private wishX = 0;
  private wishZ = 0;
  private readonly readBlock: BlockReader = (x, y, z) => this.world.getBlock(x, y, z);
  private readonly flow = [0, 0, 0];
  /** View-space bearing of the current shake, in radians (set by hurt()). */
  private hurtAngle = 0;
  /** Blocks fallen since last having ground underfoot (vanilla `fallDistance`). */
  private fallDistance = 0;
  /** Blocks fallen so far (read by the mace smash). */
  get fallen(): number { return this.fallDistance; }
  /** Cancel the pending fall (mace smash hit: no fall damage). */
  resetFall(): void { this.fallDistance = 0; }
  /** Depth Strider level on the boots (0..3), set by the game each tick. */
  depthStrider = 0;
  /** Status effects (game/effects): walk-speed multiplier, extra jump velocity, Slow Falling. Set by the game loop each tick. */
  speedMult = 1;
  jumpBoost = 0;
  slowFalling = false;
  /** Elytra gliding (player/elytra.ts); `hasElytra` is set by the game loop from the chest slot. */
  readonly glider = new Glider();
  hasElytra = false;
  /** Wall crash while gliding: damage to apply (kinetic energy). */
  onGlideCrash: ((damage: number) => void) | null = null;
  /** Ticks since lava last hurt the player. */
  private lavaTimer = 0;
  private swingTicks = -1;
  private prevLimbSwing = 0;
  private prevLimbSwingAmount = 0;
  private bodyYaw = Math.PI;
  /** Seated on a mount (game/mounts.ts): third-person sitting pose, movement tick skipped. */
  sitting = false;
  /** Lying in a bed (game/sleep.ts): movement and look locked, lying pose, pillow camera. */
  sleeping = false;
  sleepDx = 0; sleepDz = 0;
  enterBed(x: number, y: number, z: number, yaw: number, dx: number, dz: number): void {
    this.sleeping = true; this.sleepDx = dx; this.sleepDz = dz;
    this.position.set(x, y, z); this.prevPosition.copy(this.position); this.velocity.set(0, 0, 0);
    this.yaw = yaw; this.pitch = 0; this.bodyYaw = this.prevBodyYaw = yaw;
    this.sneaking = false; this.sprinting = false; this.swimming = false; this.onGround = true;
  }
  leaveBed(x: number, y: number, z: number): void {
    this.sleeping = false;
    this.position.set(x, y, z); this.prevPosition.copy(this.position); this.velocity.set(0, 0, 0);
  }
  /** Riding hook: called each tick while `sitting`; the mount drives the position. */
  rideTick: (() => void) | null = null;
  /** Movement input for a mount (same keys the touch controls inject). */
  rideInput(out: { fwd: number; strafe: number; jump: boolean; jumpTapped: boolean; sneak: boolean }): void {
    out.fwd = (this.has('KeyW') ? 1 : 0) - (this.has('KeyS') ? 1 : 0);
    out.strafe = (this.has('KeyD') ? 1 : 0) - (this.has('KeyA') ? 1 : 0);
    out.jump = this.has('Space');
    out.jumpTapped = this.pressed.has('Space');
    out.sneak = this.has('ShiftLeft', 'ShiftRight');
    this.pressed.clear();
  }
  /** Put the rider at its seat (`snap`: no interpolation from the old spot). */
  rideTo(x: number, y: number, z: number, snap = false): void {
    this.position.set(x, y, z);
    if (snap) this.prevPosition.copy(this.position);
    this.fallDistance = 0;
  }
  /** Face the body along the mount's heading. */
  setBodyYaw(y: number): void { this.bodyYaw = y; }
  private prevBodyYaw = Math.PI;
  private deadTicks = 0;
  private sprintAllowed = true;
  private moveAcc: MovementExhaustion = { walked: 0, sprinted: 0, swum: 0, jumps: 0, sprintJumps: 0 };
  private equipHeld = 0;
  private equipArmor: [number, number, number, number] = [0, 0, 0, 0];
  private readonly pose_ = {} as PlayerPoseView;
  private usingBow = false;
  /** Fire / light for the third-person pose; the lead sets them each frame. */
  onFire = false;
  light = 1;

  /** `dom` null = headless (the game server runs this physics for its agent players): no listeners, input through setKey(). */
  constructor(private readonly world: World, private readonly dom: HTMLElement | null) {
    this.camera = new THREE.PerspectiveCamera(FOV, dom ? dom.clientWidth / dom.clientHeight : 1, 0.08, 1200);
    if (!dom) return;
    document.addEventListener('keydown', (e) => this.onKey(e, true));
    document.addEventListener('keyup', (e) => this.onKey(e, false));
    window.addEventListener('blur', () => this.keys.clear());
    // Losing the mouse (Esc, or opening a GUI) drops any held key: vanilla
    // stops accepting movement input the moment a screen owns the view, and a
    // stale W would otherwise walk the player off while the chest panel is up.
    document.addEventListener('pointerlockchange', () => {
      if (document.pointerLockElement !== this.dom) { this.keys.clear(); this.pressed.clear(); }
    });
    document.addEventListener('mousemove', (e) => {
      if (document.pointerLockElement !== dom || this.dead || this.sleeping) return;
      this.yaw -= e.movementX * MOUSE_SENSITIVITY * this.sensitivity;
      this.pitch -= e.movementY * MOUSE_SENSITIVITY * this.sensitivity;
      const lim = Math.PI / 2 - 0.001;
      this.pitch = Math.max(-lim, Math.min(lim, this.pitch));
    });
  }

  /** Hold or let go of a key by its KeyboardEvent.code (headless input). */
  setKey(code: string, down: boolean): void {
    if (!down) { this.keys.delete(code); return; }
    if (!this.keys.has(code)) this.pressed.add(code);
    this.keys.add(code);
  }

  get isFrozen(): boolean { return this.frozen; }

  /** Freeze physics until the spawn chunk is ready. */
  setFrozen(f: boolean): void {
    this.frozen = f;
    if (!f) {
      this.velocity.set(0, 0, 0);
      this.prevPosition.copy(this.position);
    }
  }

  /** Spectators always fly; leaving the mode keeps flight on so you don't drop. */
  setSpectator(on: boolean): void {
    this.spectator = on;
    this.flying = true;
    this.onGround = false;
  }

  /**
   * Take damage. Runs vanilla's hurt camera shake: the view tilts and eases
   * back out over the next half second (`HURT_TILT_DEG`, dialled back from the
   * source game's 14°). `fromX`/`fromZ` is the world direction the damage came
   * from, so a hit from the side reads as a roll and one from the front as a
   * lurch; damage with no source at all (falling, fire, the void) picks a
   * random direction, like the source game.
   */
  hurt(fromX = 0, fromZ = 0): void {
    this.hurtTime = HURT_DURATION;
    // `attackedAtYaw`: the bearing of the hit relative to the yaw at the moment
    // it landed. A hit with no source rolls one way or the other at random.
    this.hurtAngle = fromX === 0 && fromZ === 0
      ? (Math.random() < 0.5 ? 0 : Math.PI)
      : Math.atan2(fromZ, fromX) - (Math.PI - this.yaw);
  }

  private onKey(e: KeyboardEvent, down: boolean): void {
    if (down) {
      this.keys.add(e.code);
      if (!e.repeat) this.pressed.add(e.code);
      if (e.code === 'KeyF' && !e.repeat && !this.spectator && !this.survival) this.flying = !this.flying;
      if (e.code === 'KeyN' && !e.repeat && !this.survival) this.setSpectator(!this.spectator);
      if (e.code === 'KeyV' && !e.repeat && !e.ctrlKey && !e.metaKey && !e.altKey
        && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)
        && (!this.cameraKeyNeedsLock || document.pointerLockElement === this.dom)) this.cycleCameraMode();
      if (e.code === 'Space' && !e.repeat) e.preventDefault();
      // Ctrl is sprint: keep Ctrl+S / Ctrl+D from opening browser dialogs mid-game
      if (e.ctrlKey && MOVE_KEYS.has(e.code) && document.pointerLockElement) e.preventDefault();
    } else {
      this.keys.delete(e.code);
    }
  }

  get isFirstPerson(): boolean { return this.cameraMode === 0; }

  /** First person -> third-person back -> third-person front -> first person. */
  cycleCameraMode(): void {
    this.cameraMode = ((this.cameraMode + 1) % 3) as 0 | 1 | 2;
  }

  /** Start the arm swing (attack / place / break); restarts if already swinging. */
  startSwing(): void { this.swingTicks = 0; this.swingProgress = 0; }

  /** Bow draw state for the third-person pose (the lead sets it). */
  setUsingBow(b: boolean): void { this.usingBow = b; }
  /** Item in use (set by the game each tick): slows walking to 20%. */
  usingItem = false;
  /** Third-person use pose (set by the game each tick). */
  useAnim: '' | 'spear' | 'spear_charge' | 'spear_tired' | 'spear_drop' | 'crossbow_charge' | 'crossbow_hold' | 'eat' = '';

  /** Held item id and armor ids (helmet, chest, legs, boots) shown by poseView(). */
  setEquipment(held: number, armor: readonly number[]): void {
    this.equipHeld = held;
    for (let i = 0; i < 4; i++) this.equipArmor[i] = armor[i] ?? 0;
  }

  /** Hunger gates sprinting (food <= 6 in vanilla); creative leaves it true. */
  setSprintAllowed(b: boolean): void { this.sprintAllowed = b; }

  /**
   * Knock the player back. (dx, dz) is the direction the player is *pushed*
   * (away from the attacker; it is normalised). Vanilla: halve the current
   * velocity, add `strength` along the direction, and pop up (<= 0.4) if grounded.
   */
  knockback(dx: number, dz: number, strength: number): void {
    const len = Math.hypot(dx, dz);
    if (len < 1e-5 || strength <= 0) return;
    const v = this.velocity;
    v.x = v.x / 2 + (dx / len) * strength;
    v.z = v.z / 2 + (dz / len) * strength;
    if (this.onGround) v.y = Math.min(0.4, v.y / 2 + strength);
  }

  /** Distance moved / jumps made since the last call, then reset — PlayerStats charges exhaustion for it. */
  movementExhaustion(): MovementExhaustion {
    const m = this.moveAcc;
    const out = { ...m };
    m.walked = m.sprinted = m.swum = m.jumps = m.sprintJumps = 0;
    return out;
  }

  /** Respawn / warp: place the feet, zero velocity and fall, unfreeze, clear death state. */
  teleportTo(x: number, y: number, z: number): void {
    this.glider.stop();
    this.position.set(x, y, z);
    this.prevPosition.copy(this.position);
    this.velocity.set(0, 0, 0);
    this.fallDistance = 0;
    this.frozen = false;
    this.dead = false;
    this.deadTicks = 0;
    this.hurtTime = 0;
    this.onGround = false;
    this.sprinting = false;
    this.swimming = false;
  }

  /**
   * Third-person body state for ENT-render's PlayerModel. The returned object
   * is reused every call (do not keep it). Body yaw trails the head like vanilla.
   */
  poseView(alpha = 1): PlayerPoseView {
    const a = this.frozen ? 1 : alpha;
    const v = this.pose_;
    const prev = this.prevPosition;
    v.x = this.position.x; v.y = this.position.y; v.z = this.position.z;
    v.px = prev.x; v.py = prev.y; v.pz = prev.z;
    const by = this.prevBodyYaw + shortestAngle(this.prevBodyYaw, this.bodyYaw) * a;
    v.yaw = by; v.headYaw = this.yaw; v.pitch = this.pitch;
    v.limbSwing = this.prevLimbSwing + (this.limbSwing - this.prevLimbSwing) * a;
    v.limbSwingAmount = this.prevLimbSwingAmount + (this.limbSwingAmount - this.prevLimbSwingAmount) * a;
    v.swing = this.swingTicks >= 0 ? Math.min(1, (this.swingTicks + a) / SWING_TICKS) : 0;
    v.sneaking = this.sneaking; v.sprinting = this.sprinting; v.swimming = (this.swimming && this.inWater) || this.glider.gliding;
    v.sitting = this.sitting; v.sleeping = this.sleeping;
    if (this.sleeping) { v.yaw = v.headYaw = this.yaw; v.pitch = 0; }
    v.flying = this.flying; v.usingBow = this.usingBow; v.useAnim = this.useAnim;
    v.elytra = this.equipArmor[1] === I.ELYTRA; v.gliding = this.glider.gliding; v.vy = this.velocity.y;
    v.elytraBroken = !this.hasElytra;
    v.hurtTime = this.hurtTime;
    v.heldItem = this.equipHeld;
    v.armor = this.equipArmor;
    v.onFire = this.onFire;
    v.light = this.light;
    return v;
  }

  private has(...codes: string[]): boolean {
    if (this.dead) return false;
    for (const c of codes) if (this.keys.has(c) || this.pressed.has(c)) return true;
    return false;
  }

  /** One 20 Hz game tick. */
  tick(): void {
    if (this.frozen) return;
    this.prevPosition.copy(this.position);
    this.prevEyeHeight = this.eyeHeight;
    this.prevStepEase = this.stepEase;
    this.stepEase *= 0.4;
    this.prevFovScale = this.fovScale;
    this.prevWalkDist = this.walkDist;
    this.prevBob = this.bob;
    this.prevLimbSwing = this.limbSwing;
    this.prevLimbSwingAmount = this.limbSwingAmount;
    this.prevBodyYaw = this.bodyYaw;
    if (this.hurtTime > 0) this.hurtTime--;
    if (this.swingTicks >= 0 && ++this.swingTicks >= SWING_TICKS) this.swingTicks = -1;
    this.swingProgress = this.swingTicks >= 0 ? this.swingTicks / SWING_TICKS : 0;
    if (this.dead) this.deadTicks++;
    if (this.survival) { this.flying = false; this.spectator = false; }
    if (this.sleeping) {
      this.velocity.set(0, 0, 0); this.fallDistance = 0; this.sprinting = false; this.sneaking = false; this.swimming = false;
      return;
    }
    if (this.sitting && this.rideTick) {
      this.fallDistance = 0; this.velocity.set(0, 0, 0); this.sprinting = false; this.swimming = false;
      this.rideTick();
      return;
    }

    const jumpKey = this.has('Space');
    const sprintKey = this.has('ControlLeft', 'ControlRight');
    this.shiftDown = this.has('ShiftLeft', 'ShiftRight');
    let forward = (this.has('KeyW') ? 1 : 0) - (this.has('KeyS') ? 1 : 0);
    let strafe = (this.has('KeyD') ? 1 : 0) - (this.has('KeyA') ? 1 : 0);
    const forwardTapped = this.pressed.has('KeyW');
    const jumpTapped = this.pressed.has('Space');
    this.pressed.clear();

    this.updateFluids();
    this.glider.update({
      onGround: this.onGround, inLiquid: this.inWater || this.inLava, flying: this.flying, spectator: this.spectator,
      hasElytra: this.hasElytra, jumpTapped, vy: this.velocity.y,
    });
    // lava damages you every 10 ticks in vanilla, which keeps re-triggering the
    // hurt shake for as long as you stand in it
    if (this.inLava && !this.spectator && !this.survival) {
      if (this.lavaTimer === 0) this.hurt();
      this.lavaTimer = (this.lavaTimer + 1) % HURT_DURATION;
    } else {
      this.lavaTimer = 0;
    }
    if (this.flying) this.swimming = false;
    else if (this.swimming) this.swimming = this.sprinting && this.inWater;
    else this.swimming = this.sprinting && this.eyeInWater;

    // sneaking (or crawling out of water in the swim pose) slows input
    if (this.pose === CROUCH || (this.pose === SWIM && !this.inWater)) {
      forward *= SNEAK_INPUT_SCALE;
      strafe *= SNEAK_INPUT_SCALE;
    }

    // --- sprint: hold Ctrl or double-tap forward ---
    const hasForward = this.eyeInWater ? forward > 1e-5 : forward >= 0.8;
    const canSprint = (!this.inWater || this.eyeInWater) && (this.sprintAllowed || this.flying);
    if (this.sprintTapTimer > 0) this.sprintTapTimer--;
    if (forwardTapped && hasForward && !this.sprinting && canSprint
      && (this.onGround || this.eyeInWater || this.flying)) {
      if (this.sprintTapTimer <= 0 && !sprintKey) this.sprintTapTimer = DOUBLE_TAP_TICKS;
      else this.sprinting = true;
    }
    if (!this.sprinting && canSprint && hasForward && sprintKey) this.sprinting = true;
    if (this.sprinting && !this.sprintAllowed && !this.flying) this.sprinting = false;
    if (this.sprinting) {
      if (this.swimming) {
        if ((!this.onGround && !this.shiftDown && !hasForward) || !this.inWater) this.sprinting = false;
      } else if (!hasForward || (this.horizontalCollision && !this.minorCollision)
        || (this.inWater && !this.eyeInWater)) {
        this.sprinting = false;
      }
    }

    // --- creative flight: double-tap jump toggles, jump/sneak move vertically ---
    if (this.flyTapTimer > 0) this.flyTapTimer--;
    if (jumpTapped) {
      if (this.flyTapTimer === 0) {
        this.flyTapTimer = DOUBLE_TAP_TICKS;
      } else {
        if (!this.spectator && !this.survival) this.flying = !this.flying;
        this.flyTapTimer = 0;
      }
    }
    const v = this.velocity;
    if (this.flying) {
      v.y += ((jumpKey ? 1 : 0) - (this.shiftDown ? 1 : 0)) * FLY_SPEED * 3;
    } else if (this.shiftDown && this.inWater) {
      v.y -= LIQUID_SWIM_IMPULSE;
    }

    // --- jumping ---
    if (this.jumpDelay > 0) this.jumpDelay--;
    if (Math.abs(v.x) < 0.003) v.x = 0;
    if (Math.abs(v.y) < 0.003) v.y = 0;
    if (Math.abs(v.z) < 0.003) v.z = 0;

    const fx = -Math.sin(this.yaw);
    const fz = -Math.cos(this.yaw);
    if ((jumpKey || this.wantsAutoJump(forward, strafe)) && !this.flying) {
      const liquidHeight = this.inLava ? this.lavaHeight : this.waterHeight;
      const threshold = POSE_EYE[this.pose] < 0.4 ? 0 : 0.4;
      if ((this.inWater || this.inLava) && (!this.onGround || liquidHeight > threshold)) {
        v.y += LIQUID_SWIM_IMPULSE;
      } else if (this.onGround && this.jumpDelay === 0) {
        v.y = JUMP_VELOCITY + this.jumpBoost;
        if (this.sprinting) this.moveAcc.sprintJumps++; else this.moveAcc.jumps++;
        if (this.sprinting) {
          v.x += fx * SPRINT_JUMP_BOOST;
          v.z += fz * SPRINT_JUMP_BOOST;
        }
        this.jumpDelay = JUMP_DELAY_TICKS;
      }
    } else {
      this.jumpDelay = 0;
    }

    // --- travel ---
    forward *= INPUT_SCALE;
    strafe *= INPUT_SCALE;
    // vanilla: using an item (bow draw, eating, shield up, crossbow load) scales input by 0.2
    if (this.usingItem && !this.flying) { forward *= 0.2; strafe *= 0.2; }
    // right = (-fz, fx)
    this.wishX = forward * fx - strafe * fz;
    this.wishZ = forward * fz + strafe * fx;

    if (this.swimming) {
      // swimming steers vertically toward the look direction
      const lookY = Math.sin(this.pitch);
      const p = this.position;
      const above = this.world.getBlock(Math.floor(p.x), Math.floor(p.y + 0.9), Math.floor(p.z));
      if (lookY <= 0 || jumpKey || IS_WATERY[above] === 1 || above === LAVA_ID) {
        v.y += (lookY - v.y) * (lookY < -0.2 ? 0.085 : 0.06);
      }
    }
    const y0 = this.position.y;
    if (this.flying) {
      const vy = v.y;
      this.travel();
      v.y = vy * FLY_VERTICAL_DRAG;
      if (this.onGround) this.flying = false;
    } else {
      this.travel();
    }
    this.updateFall(this.position.y - y0);

    // View bob (vanilla `walkDist` / `bob`): the phase advances with distance
    // walked, so the sway speeds up when you sprint, and the amplitude is the
    // smoothed ground speed, capped — airborne and swimming decay it to zero.
    const stepX = this.position.x - this.prevPosition.x;
    const stepZ = this.position.z - this.prevPosition.z;
    const step = Math.sqrt(stepX * stepX + stepZ * stepZ);
    this.walkDist += step * WALK_DIST_SCALE;
    this.trackMotion(stepX, stepZ, step);
    this.bob += ((this.onGround && !this.swimming ? Math.min(BOB_SPEED_CAP, step) : 0) - this.bob) * 0.4;

    if (this.position.y < -8 && !this.spectator && !this.survival) {
      // fell out of the world (shouldn't happen — bedrock) — respawn
      this.position.set(this.position.x, WORLD_HEIGHT - 20, this.position.z);
      this.prevPosition.copy(this.position);
      this.velocity.set(0, 0, 0);
      this.hurt();
    }

    this.updatePose();
    this.eyeHeight += (POSE_EYE[this.pose] - this.eyeHeight) * 0.5;
    const fovTarget = this.fovModifier();
    this.fovScale += (fovTarget - this.fovScale) * 0.5;
    this.fovPulse *= 0.8; // ~0.3 s ease-out
    if (this.fovPulse < 0.01) this.fovPulse = 0;
  }

  /** Limb swing, body yaw and exhaustion bookkeeping for this tick's movement. */
  private trackMotion(dx: number, dz: number, step: number): void {
    const f = Math.min(step * 4, 1);
    this.limbSwingAmount += (f - this.limbSwingAmount) * 0.4;
    this.limbSwing += this.limbSwingAmount;

    // body yaw: toward the movement direction (flipped when walking backwards),
    // never more than 75 degrees off the head — vanilla `tickHeadTurn`
    let target = this.bodyYaw;
    if (step * step > 0.0025 * 0.0025 && !this.flying) {
      const move = Math.atan2(-dx, -dz);
      const off = Math.abs(shortestAngle(this.yaw, move));
      target = off > 95 * DEG ? move + Math.PI : move;
      this.bodyYaw += shortestAngle(this.bodyYaw, target) * 0.3;
    }
    if (this.swingTicks >= 0) this.bodyYaw = this.yaw;
    const diff = shortestAngle(this.bodyYaw, this.yaw);
    const lim = 75 * DEG;
    if (diff > lim) this.bodyYaw = this.yaw - lim;
    else if (diff < -lim) this.bodyYaw = this.yaw + lim;

    if (this.flying || this.spectator) return;
    const m = this.moveAcc;
    if (this.inWater || this.inLava) {
      m.swum += Math.hypot(dx, dz, this.position.y - this.prevPosition.y);
    } else if (this.onGround) {
      if (this.sprinting) m.sprinted += step; else m.walked += step;
    }
  }

  /** Apply input acceleration, move, then gravity and drag for the current medium. */
  private travel(): void {
    const v = this.velocity;
    const p = this.position;
    const falling = v.y <= 0;

    if (this.glider.gliding) {
      this.glider.step({ velocity: v, yaw: this.yaw, pitch: this.pitch });
      this.move();
      const dmg = this.glider.crash(Math.hypot(v.x, v.z));
      if (dmg > 0 && this.horizontalCollision) this.onGlideCrash?.(dmg);
      return;
    }

    if ((this.inWater || this.inLava) && !this.flying) {
      const y0 = p.y;
      // Depth Strider: blend water accel/drag toward land values (level/3, halved off the ground)
      const ds = this.inWater ? (Math.min(3, this.depthStrider) / 3) * (this.onGround ? 1 : 0.5) : 0;
      this.accelerate(LIQUID_ACCEL + (WALK_SPEED - LIQUID_ACCEL) * ds);
      this.move();
      if (this.inWater) {
        const drag0 = this.sprinting ? WATER_SPRINT_DRAG : WATER_DRAG;
        const drag = drag0 + (0.546 - drag0) * ds;
        v.x *= drag;
        v.y *= WATER_DRAG;
        v.z *= drag;
        this.sinkInLiquid(falling);
      } else {
        if (this.lavaHeight <= 0.4) {
          v.x *= LAVA_DRAG;
          v.y *= WATER_DRAG;
          v.z *= LAVA_DRAG;
          this.sinkInLiquid(falling);
        } else {
          v.multiplyScalar(LAVA_DRAG);
        }
        v.y -= GRAVITY / 4;
      }
      // pushing against a bank with open space above: hop out
      if (this.horizontalCollision && this.isFree(v.x, v.y + 0.6 - p.y + y0, v.z)) {
        v.y = LIQUID_EXIT_VELOCITY;
      }
      return;
    }

    const below = this.world.getBlock(Math.floor(p.x), Math.floor(p.y - 0.5000001), Math.floor(p.z));
    const slip = SLIPPERINESS[below];
    const drag = this.onGround ? slip * HORIZONTAL_DRAG : HORIZONTAL_DRAG;
    let accel: number;
    if (this.onGround) {
      const speed = WALK_SPEED * (this.sprinting ? SPRINT_SPEED_MULT : 1) * this.speedMult;
      accel = speed * (0.21600002 / (slip * slip * slip));
    } else if (this.flying) {
      accel = this.sprinting ? FLY_SPEED * 2 : FLY_SPEED;
    } else {
      accel = this.sprinting ? SPRINT_AIR_ACCEL : AIR_ACCEL;
    }
    this.accelerate(accel);
    // climbable cells (weeping / twisting vines): vanilla LivingEntity.handleOnClimbable
    const climbing = !this.flying && CLIMBABLE_NETHER.has(this.world.getBlock(Math.floor(p.x), Math.floor(p.y), Math.floor(p.z)));
    if (climbing) {
      v.x = Math.max(-0.15, Math.min(0.15, v.x));
      v.z = Math.max(-0.15, Math.min(0.15, v.z));
      v.y = Math.max(v.y, this.shiftDown ? 0 : -0.15);
      this.fallDistance = 0;
    }
    this.move();
    if (climbing && (this.horizontalCollision || this.has('Space'))) v.y = 0.2;
    // Slow Falling: gravity 0.01 while descending (vanilla), and no fall damage (see updateFall)
    v.y = (v.y - (this.slowFalling && v.y <= 0 ? 0.01 : GRAVITY)) * VERTICAL_DRAG;
    v.x *= drag;
    v.z *= drag;
  }

  /**
   * Vanilla fall tracking: distance accumulates while airborne and, on landing
   * past the fall-damage threshold, hurts — which is what shakes the camera.
   * Water, lava, flight and spectator all reset it, as in the source game.
   */
  private updateFall(dy: number): void {
    if (this.glider.gliding && this.velocity.y > -0.5) this.fallDistance = 1;
    if (this.flying || this.spectator || this.inWater || this.inLava || this.slowFalling) {
      this.fallDistance = 0;
    } else if (this.onGround) {
      if (this.fallDistance > 0 && this.onFall) this.onFall(this.fallDistance);
      else if (this.fallDistance > FALL_HURT_DISTANCE) this.hurt();
      this.fallDistance = 0;
    } else if (dy < 0) {
      this.fallDistance -= dy;
    }
  }

  /** Add this tick's input to the velocity; input longer than 1 is normalized. */
  private accelerate(accel: number): void {
    const lenSq = this.wishX * this.wishX + this.wishZ * this.wishZ;
    if (lenSq < 1e-7) return;
    const k = lenSq > 1 ? accel / Math.sqrt(lenSq) : accel;
    this.velocity.x += this.wishX * k;
    this.velocity.z += this.wishZ * k;
  }

  /** Liquid buoyancy: slow sinking, except while sprint-swimming. */
  private sinkInLiquid(falling: boolean): void {
    if (this.sprinting) return;
    const v = this.velocity;
    const sink = GRAVITY / 16;
    v.y = falling && Math.abs(v.y - 0.005) >= 0.003 && Math.abs(v.y - sink) < 0.003 ? -0.003 : v.y - sink;
  }

  /** Bedrock auto-jump: walking into a full-block ledge with headroom (and not steppable). */
  private wantsAutoJump(forward: number, strafe: number): boolean {
    if (!this.autoJump || !this.onGround || this.flying || this.spectator || this.sitting || this.shiftDown) return false;
    if (this.swimming || this.inWater || this.inLava || this.glider.gliding || this.jumpDelay > 0) return false;
    if (this.pose === CROUCH || (forward === 0 && strafe === 0)) return false;
    const sn = Math.sin(this.yaw), cs = Math.cos(this.yaw);
    let dx = -sn * forward + cs * strafe, dz = -cs * forward - sn * strafe;
    const len = Math.hypot(dx, dz);
    if (len < 1e-5) return false;
    dx = (dx / len) * 0.12; dz = (dz / len) * 0.12;
    if (!this.overlapsSolid(this.hitbox(dx, 0.02, dz))) return false;           // nothing in the way
    if (!this.overlapsSolid(this.hitbox(dx, STEP_HEIGHT + 0.02, dz))) return false; // steppable
    if (this.overlapsSolid(this.hitbox(dx, 1.02, dz))) return false;           // taller than ~1 block
    return !this.overlapsSolid(this.hitbox(0, 1.02, 0));                        // headroom for the hop
  }

  /** Move by the current velocity, clipping against solid blocks. */
  private move(): void {
    const v = this.velocity;
    const p = this.position;
    let dx = v.x;
    let dy = v.y;
    let dz = v.z;

    if (this.spectator) {
      p.x += dx;
      p.y += dy;
      p.z += dz;
      this.horizontalCollision = false;
      this.minorCollision = false;
      this.onGround = false;
      return;
    }

    // sneaking never walks off an edge
    if (this.shiftDown && !this.flying && this.onGround && dy <= 0) {
      const step = 0.05;
      const shrink = (d: number) => (Math.abs(d) <= step ? 0 : d - Math.sign(d) * step);
      while (dx !== 0 && this.isUnsupported(dx, 0)) dx = shrink(dx);
      while (dz !== 0 && this.isUnsupported(0, dz)) dz = shrink(dz);
      while (dx !== 0 && dz !== 0 && this.isUnsupported(dx, dz)) {
        dx = shrink(dx);
        dz = shrink(dz);
      }
    }

    const wantX = dx;
    const wantY = dy;
    const wantZ = dz;
    const box = [p.x - HALF_W, p.y, p.z - HALF_W, p.x + HALF_W, p.y + this.height, p.z + HALF_W];
    dy = this.clip(box, 1, dy);
    box[1] += dy;
    box[4] += dy;
    const zFirst = Math.abs(dx) < Math.abs(dz);
    if (zFirst) {
      dz = this.clip(box, 2, dz);
      box[2] += dz;
      box[5] += dz;
    }
    dx = this.clip(box, 0, dx);
    box[0] += dx;
    box[3] += dx;
    if (!zFirst) dz = this.clip(box, 2, dz);
    const wasGround = this.onGround;
    let stepped = false;
    // step-up (vanilla step height 0.6): blocked on the ground -> retry raised, then settle down
    if ((dx !== wantX || dz !== wantZ) && !this.flying && wantY <= 0 && (wasGround || (dy !== wantY))) {
      const b2 = [p.x - HALF_W, p.y, p.z - HALF_W, p.x + HALF_W, p.y + this.height, p.z + HALF_W];
      const up = this.clip(b2, 1, STEP_HEIGHT);
      b2[1] += up; b2[4] += up;
      let sx = wantX, sz = wantZ;
      if (Math.abs(wantX) < Math.abs(wantZ)) {
        sz = this.clip(b2, 2, sz); b2[2] += sz; b2[5] += sz;
        sx = this.clip(b2, 0, sx); b2[0] += sx; b2[3] += sx;
      } else {
        sx = this.clip(b2, 0, sx); b2[0] += sx; b2[3] += sx;
        sz = this.clip(b2, 2, sz); b2[2] += sz; b2[5] += sz;
      }
      const down = this.clip(b2, 1, -up);
      if (sx * sx + sz * sz > dx * dx + dz * dz + 1e-9 && up + down > 1e-4) {
        const gain = up + down;
        dx = sx; dz = sz; dy = gain;
        this.stepEase += gain;
        stepped = true;
      }
    }
    p.x += dx;
    p.y += dy;
    p.z += dz;

    const hitX = dx !== wantX;
    const hitZ = dz !== wantZ;
    this.horizontalCollision = hitX || hitZ;
    this.onGround = (dy !== wantY && wantY < 0) || stepped;
    if (hitX) v.x = 0;
    if (hitZ) v.z = 0;
    if (dy !== wantY && !stepped) v.y = 0;

    // a glancing blow (within 8° of the input direction) doesn't break a sprint
    this.minorCollision = false;
    if (this.horizontalCollision) {
      const wish = this.wishX * this.wishX + this.wishZ * this.wishZ;
      const moved = dx * dx + dz * dz;
      if (wish >= 1e-5 && moved >= 1e-5) {
        const cos = (this.wishX * dx + this.wishZ * dz) / Math.sqrt(wish * moved);
        this.minorCollision = Math.acos(Math.min(1, cos)) < 0.13962634;
      }
    }

    if (!this.flying) {
      const below = this.world.getBlock(Math.floor(p.x), Math.floor(p.y - 0.5000001), Math.floor(p.z));
      v.x *= SPEED_FACTOR[below];
      v.z *= SPEED_FACTOR[below];
    }
  }

  /**
   * Largest part of a move of `d` along `axis` (0=x, 1=y, 2=z) the box can
   * make before hitting a block's collision shape — vanilla's `AABB.collide`.
   */
  private clip(box: number[], axis: number, d: number): number {
    return clipMove(box, axis, d, EPS, this.shapesAt);
  }

  /** Does any block's collision shape overlap the world-space box
   *  [x0,y0,z0,x1,y1,z1]? Shapes are tested, not whole cells, so the hitbox can
   *  stand on a bed's mattress or in the air over a chest's 1/16 margin. */
  private overlapsSolid(box: readonly number[]): boolean {
    return boxHits(box, EPS, this.shapesAt);
  }

  /** Does `test` hold for any block cell the hitbox overlaps, offset by (dx, dy, dz)? */
  private boxTouches(
    test: (x: number, y: number, z: number) => boolean,
    dx: number, dy: number, dz: number, height = this.height, inset = EPS
  ): boolean {
    const p = this.position;
    const x1 = Math.floor(p.x + dx + HALF_W - inset);
    const y1 = Math.floor(p.y + dy + height - inset);
    const z1 = Math.floor(p.z + dz + HALF_W - inset);
    for (let y = Math.floor(p.y + dy + inset); y <= y1; y++) {
      for (let z = Math.floor(p.z + dz - HALF_W + inset); z <= z1; z++) {
        for (let x = Math.floor(p.x + dx - HALF_W + inset); x <= x1; x++) {
          if (test(x, y, z)) return true;
        }
      }
    }
    return false;
  }

  private readonly liquidTest = (x: number, y: number, z: number): boolean => {
    const id = this.world.getBlock(x, y, z);
    return IS_WATERY[id] === 1 || IS_LAVA[id] === 1;
  };

  /** Collision boxes (1/16 units) of the block in a cell; the world floor is
   *  solid and the sky above it is empty. */
  private cellCollision(x: number, y: number, z: number): Float32Array {
    if (y < 0) return FULL_CELL_BOXES;
    if (y >= WORLD_HEIGHT) return NO_BOXES;
    return collisionOf(this.world.getBlock(x, y, z));
  }

  private readonly shapesAt: ShapesAt = (x, y, z) => this.cellCollision(x, y, z);

  /** The hitbox, offset by (dx, dy, dz): [x0, y0, z0, x1, y1, z1]. */
  private hitbox(dx: number, dy: number, dz: number, height = this.height): number[] {
    const p = this.position;
    return [
      p.x + dx - HALF_W, p.y + dy, p.z + dz - HALF_W,
      p.x + dx + HALF_W, p.y + dy + height, p.z + dz + HALF_W,
    ];
  }

  /** Would a horizontal move of (dx, dz) leave nothing within a step below the feet? */
  private isUnsupported(dx: number, dz: number): boolean {
    return !this.overlapsSolid(this.hitbox(dx, -STEP_HEIGHT, dz));
  }

  /** Is the hitbox, offset by (dx, dy, dz), clear of both blocks and liquids? */
  private isFree(dx: number, dy: number, dz: number): boolean {
    return !this.overlapsSolid(this.hitbox(dx, dy, dz)) && !this.boxTouches(this.liquidTest, dx, dy, dz);
  }

  /** Is the cell filled with `liquid`? Kelp / seagrass cells count as water. */
  private isLiquid(x: number, y: number, z: number, liquid: number): boolean {
    const id = this.world.getBlock(x, y, z);
    return id === liquid || (liquid === WATER_ID && IS_WATERY[id] === 1) || (liquid === LAVA_ID && IS_LAVA[id] === 1);
  }

  /** Top of the liquid in a cell: full if more of it sits above, else its level (8/9 for a source). */
  private liquidSurface(x: number, y: number, z: number, liquid: number): number {
    if (this.isLiquid(x, y + 1, z, liquid)) return y + 1;
    return y + (liquid === WATER_ID ? WATER_AMOUNT[this.world.getBlock(x, y, z)] / 9 : Math.min(LIQUID_SURFACE_HEIGHT, LAVA_AMOUNT[this.world.getBlock(x, y, z)] / 9));
  }

  /**
   * How far the given liquid rises above the feet (0 when not touching it).
   * Flowing water also pushes the player along its current.
   */
  private liquidHeight(liquid: number): number {
    const minY = this.position.y + 0.001;
    const pushed = liquid === WATER_ID && !this.flying;
    const flow = this.flow;
    let h = 0;
    let px = 0, py = 0, pz = 0, cells = 0;
    this.boxTouches((x, y, z) => {
      if (this.isLiquid(x, y, z, liquid)) {
        const surface = this.liquidSurface(x, y, z, liquid);
        if (surface >= minY) {
          h = Math.max(h, surface - minY);
          if (pushed) {
            waterFlow(this.readBlock, x, y, z, flow);
            // barely wading: the current has little to push against
            const k = h < 0.4 ? h : 1;
            px += flow[0] * k;
            py += flow[1] * k;
            pz += flow[2] * k;
            cells++;
          }
        }
      }
      return false;
    }, 0, 0, 0, this.height, 0.001);

    if (cells > 0 && (px !== 0 || py !== 0 || pz !== 0)) {
      const v = this.velocity;
      let k = WATER_PUSH / cells;
      const len = Math.hypot(px, py, pz) * k;
      if (Math.abs(v.x) < 0.003 && Math.abs(v.z) < 0.003 && len < MIN_WATER_PUSH) k *= MIN_WATER_PUSH / len;
      v.x += px * k;
      v.y += py * k;
      v.z += pz * k;
    }
    return h;
  }

  private updateFluids(): void {
    const p = this.position;
    this.waterHeight = this.liquidHeight(WATER_ID);
    this.lavaHeight = this.liquidHeight(LAVA_ID);
    this.inWater = this.waterHeight > 0;
    this.inLava = this.lavaHeight > 0;

    const bx = Math.floor(p.x);
    const bz = Math.floor(p.z);
    const eyeY = p.y + POSE_EYE[this.pose];
    const ey = Math.floor(eyeY);
    this.headInWater = IS_LAVA[this.world.getBlock(bx, ey, bz)] === 1
      || (this.isLiquid(bx, ey, bz, WATER_ID) && this.liquidSurface(bx, ey, bz, WATER_ID) > eyeY);
    // "underwater" for sprint-swimming is tested slightly below the eyes
    const testY = eyeY - 1 / 9;
    const cy = Math.floor(testY);
    this.eyeInWater = this.isLiquid(bx, cy, bz, WATER_ID)
      && this.liquidSurface(bx, cy, bz, WATER_ID) > testY;
  }

  private fits(pose: number): boolean {
    return !this.overlapsSolid(this.hitbox(0, 0, 0, POSE_HEIGHT[pose]));
  }

  /** Stand / crouch / swim hitbox; stays low while there's no room to stand up. */
  private updatePose(): void {
    if (!this.spectator && !this.fits(SWIM)) return;
    const want = this.swimming ? SWIM : this.shiftDown && !this.flying ? CROUCH : STAND;
    this.pose = this.spectator ? STAND : this.fits(want) ? want : this.fits(CROUCH) ? CROUCH : SWIM;
    this.height = POSE_HEIGHT[this.pose];
    this.sneaking = this.pose === CROUCH;
  }

  /**
   * Call every frame. `alpha` is how far the frame is between the previous
   * game tick (0) and the current one (1).
   */
  updateCamera(alpha = 1): void {
    const a = this.frozen ? 1 : alpha;
    const prev = this.prevPosition;
    this.swingProgress = this.swingTicks >= 0 ? Math.min(1, (this.swingTicks + a) / SWING_TICKS) : 0;
    let eye = this.prevEyeHeight + (this.eyeHeight - this.prevEyeHeight) * a;
    eye -= this.prevStepEase + (this.stepEase - this.prevStepEase) * a;
    // dying: the eyes sink to the floor (vanilla's DYING pose, eye height 0.2)
    if (this.dead) eye += (DEAD_EYE - eye) * Math.min(1, (this.deadTicks + a) / 10);
    this.camera.position.set(
      prev.x + (this.position.x - prev.x) * a,
      prev.y + (this.position.y - prev.y) * a + eye,
      prev.z + (this.position.z - prev.z) * a
    );
    // in bed: eyes on the pillow, lying flat, looking up and along the bed
    const viewPitch = this.sleeping ? (this.cameraMode === 0 ? 0.6 : -0.4) : this.pitch;
    if (this.sleeping) this.camera.position.set(this.position.x + this.sleepDx * 0.55, this.position.y + 0.3, this.position.z + this.sleepDz * 0.55);
    const dirV = new THREE.Vector3(
      -Math.sin(this.yaw) * Math.cos(viewPitch),
      Math.sin(viewPitch),
      -Math.cos(this.yaw) * Math.cos(viewPitch)
    );
    const first = this.cameraMode === 0;
    if (first) {
      this.camera.lookAt(this.camera.position.clone().add(dirV));
    } else {
      // Third person: pull back (or around to the front) along the look
      // direction, shortened so the lens never ends up inside a wall.
      const front = this.cameraMode === 2;
      CAM_EYE.copy(this.camera.position);
      CAM_DIR.copy(dirV);
      if (!front) CAM_DIR.negate();
      const dist = this.clearDistance(CAM_EYE, CAM_DIR, THIRD_PERSON_DIST);
      this.camera.position.copy(CAM_EYE).addScaledVector(CAM_DIR, dist);
      this.camera.lookAt(CAM_EYE);
    }

    // death view (vanilla bobHurt): the camera rolls over as the body falls
    if (this.dead) {
      const f = this.deadTicks + a;
      this.camera.quaternion.multiply(BOB_ROT.setFromAxisAngle(Z_AXIS, (40 - 8000 / (f + 200)) * DEG));
    }

    // View bob (vanilla GameRenderer.bobView): the head sways sideways, lifts
    // and rolls with the stride. Applied in view space, like the hurt tilt.
    const phase = -(this.prevWalkDist + (this.walkDist - this.prevWalkDist) * a) * Math.PI;
    const bob = this.prevBob + (this.bob - this.prevBob) * a;
    this.bobPhase = phase;
    this.bobAmount = first && this.viewBobbing ? bob : 0;
    if (first && this.viewBobbing && bob > 1e-4) {
      const sway = Math.sin(phase) * bob;
      this.camera.quaternion.multiply(
        BOB_ROT.setFromAxisAngle(X_AXIS, Math.abs(Math.cos(phase - 0.2) * bob) * 5 * DEG));
      this.camera.quaternion.multiply(BOB_ROT.setFromAxisAngle(Z_AXIS, sway * 3 * DEG));
      this.camera.translateX(-sway * 0.5);
      this.camera.translateY(Math.abs(Math.cos(phase) * bob));
    }

    // Vanilla hurt camera (GameRenderer.hurtCameraEffect): the tilt ramps in
    // and eases back to nothing over the 10 hurt ticks, peaking at 14° about a
    // horizontal axis in view space that `hurt()` set from the hit's bearing.
    const hurt = this.hurtTime - a;
    if (first && hurt > 0) {
      const t = hurt / HURT_DURATION;
      HURT_AXIS.set(Math.sin(this.hurtAngle), 0, Math.cos(this.hurtAngle));
      this.camera.quaternion.multiply(HURT_ROT.setFromAxisAngle(HURT_AXIS, Math.sin(t * t * t * t * Math.PI) * HURT_TILT_DEG * DEG));
    }

    // sprinting / flying widens the field of view
    const fov = this.baseFov * (this.prevFovScale + (this.fovScale - this.prevFovScale) * a) + this.fovPulse;
    if (Math.abs(this.camera.fov - fov) > 1e-4) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
  }

  /**
   * How far from `origin` along unit `dir` the third-person lens may sit: the
   * shortest solid-block hit over the eight corner rays of a 0.2 cube, so the
   * 0.08 near plane never pokes into a wall (vanilla `Camera.getMaxZoom`).
   */
  private clearDistance(origin: THREE.Vector3, dir: THREE.Vector3, max: number): number {
    let best = max;
    for (const [ox, oy, oz] of CAM_OFFSETS) {
      CAM_ORIGIN.set(origin.x + ox, origin.y + oy, origin.z + oz);
      const hit = raycast(this.world, CAM_ORIGIN, dir, best, IS_SOLID);
      if (!hit) continue;
      let t = 0;
      if (hit.nx !== 0) t = ((hit.nx > 0 ? hit.x + 1 : hit.x) - CAM_ORIGIN.x) / dir.x;
      else if (hit.ny !== 0) t = ((hit.ny > 0 ? hit.y + 1 : hit.y) - CAM_ORIGIN.y) / dir.y;
      else if (hit.nz !== 0) t = ((hit.nz > 0 ? hit.z + 1 : hit.z) - CAM_ORIGIN.z) / dir.z;
      if (t < best) best = Math.max(0, t);
    }
    return best;
  }

  get eyeBlock(): number {
    return this.world.getBlock(
      Math.floor(this.position.x),
      Math.floor(this.position.y + POSE_EYE[this.pose]),
      Math.floor(this.position.z)
    );
  }
}

/** Amanatides & Woo voxel traversal. Returns the hit block and entry face.
 *  Targets any breakable block (not just solid ones) so torches and plants
 *  can be clicked; liquids are pass-through. */
export interface RayHit {
  x: number;
  y: number;
  z: number;
  /** Face normal of the hit (pointing towards the ray origin). */
  nx: number;
  ny: number;
  nz: number;
  id: number;
}

/**
 * Ray vs. the boxes (1/16 units) of a block whose cell origin is (x, y, z) — not always whole numbers:
 * a jittered plant's boxes move with it. Returns the axis (0..2) of the face the nearest box is
 * entered through, -1 if the ray starts inside one, -2 on a miss.
 */
function boxesEntry(
  o: THREE.Vector3, d: THREE.Vector3, x: number, y: number, z: number, boxes: Float32Array, maxDist: number
): number {
  let best = Infinity, bestAxis = -2;
  const cell = [x, y, z];
  for (let k = 0; k < boxes.length; k += 6) {
    let tMin = 0, tMax = maxDist, axis = -1, hit = true;
    for (let a = 0; a < 3 && hit; a++) {
      const lo = cell[a] + boxes[k + a] / 16, hi = cell[a] + boxes[k + a + 3] / 16;
      const oa = o.getComponent(a), da = d.getComponent(a);
      if (Math.abs(da) < 1e-9) { if (oa < lo || oa > hi) hit = false; continue; }
      let t0 = (lo - oa) / da, t1 = (hi - oa) / da;
      if (t0 > t1) { const t = t0; t0 = t1; t1 = t; }
      if (t0 > tMin) { tMin = t0; axis = a; }
      if (t1 < tMax) tMax = t1;
      if (tMin > tMax) hit = false;
    }
    if (!hit) continue;
    if (axis === -1) return -1;
    if (tMin < best) { best = tMin; bestAxis = axis; }
  }
  return bestAxis;
}

export function raycast(
  world: World, origin: THREE.Vector3, dir: THREE.Vector3, maxDist: number,
  /** Blocks the ray stops at, by id (an empty bucket also stops at water sources). */
  targets: Uint8Array = IS_TARGETABLE
): RayHit | null {
  let x = Math.floor(origin.x);
  let y = Math.floor(origin.y);
  let z = Math.floor(origin.z);
  const stepX = dir.x > 0 ? 1 : -1;
  const stepY = dir.y > 0 ? 1 : -1;
  const stepZ = dir.z > 0 ? 1 : -1;

  const tDeltaX = Math.abs(1 / dir.x);
  const tDeltaY = Math.abs(1 / dir.y);
  const tDeltaZ = Math.abs(1 / dir.z);

  const distToBoundary = (p: number, s: number) => (s > 0 ? Math.ceil(p) - p : p - Math.floor(p));

  let tMaxX = tDeltaX * distToBoundary(origin.x, stepX);
  let tMaxY = tDeltaY * distToBoundary(origin.y, stepY);
  let tMaxZ = tDeltaZ * distToBoundary(origin.z, stepZ);

  let nx = 0, ny = 0, nz = 0;

  for (let i = 0; i < 256; i++) {
    const id = world.getBlock(x, y, z);
    if (targets[id] === 1) {
      const tb = TARGET_BOXES[id];
      if (!tb) return { x, y, z, nx, ny, nz, id };
      // smaller than its cell (torch, mushroom, slab, ground cover...): only the real shape is
      // selectable, and a ray that misses it carries on to whatever is behind
      const jit = PLANT_JITTER[id] === 1;
      const axis = boxesEntry(
        origin, dir, jit ? x + plantJitterX(x, z) : x, y, jit ? z + plantJitterZ(x, z) : z, tb, maxDist
      );
      if (axis >= 0) {
        return {
          x, y, z, id,
          nx: axis === 0 ? -stepX : 0, ny: axis === 1 ? -stepY : 0, nz: axis === 2 ? -stepZ : 0,
        };
      }
      if (axis === -1) return { x, y, z, nx, ny, nz, id };
    }
    if (tMaxX < tMaxY && tMaxX < tMaxZ) {
      if (tMaxX > maxDist) break;
      x += stepX; tMaxX += tDeltaX; nx = -stepX; ny = 0; nz = 0;
    } else if (tMaxY < tMaxZ) {
      if (tMaxY > maxDist) break;
      y += stepY; tMaxY += tDeltaY; nx = 0; ny = -stepY; nz = 0;
    } else {
      if (tMaxZ > maxDist) break;
      z += stepZ; tMaxZ += tDeltaZ; nx = 0; ny = 0; nz = -stepZ;
    }
    if (y < -2 || y > WORLD_HEIGHT + 2) break;
  }
  return null;
}
